import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Hammer, AlertTriangle, FlaskConical } from 'lucide-react';

const STATUS_COLOR = {
  ok:       'text-buy border-buy',
  warnings: 'text-hold border-hold',
  failed:   'text-sell border-sell',
};

const LINT_DOT = {
  true:  'bg-buy',
  false: 'bg-sell',
};

/**
 * PreviewBuilderSection — Phase 51.D Lab section.
 * Operator picks a code diff → backend runs static analysis (lint +
 * conflict checks) and stores a preview build. Read-only in preview;
 * write actions activate on the production domain.
 */
export default function PreviewBuilderSection({ isProd, onSwitchTab }) {
  const [previews, setPreviews]   = useState([]);
  const [diffs, setDiffs]         = useState([]);
  const [diffId, setDiffId]       = useState('');
  const [busy, setBusy]           = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = () =>
    api.get('/lab/preview/list?limit=50')
      .then(({ data }) => setPreviews(data.previews || []))
      .catch(() => {});

  useEffect(() => {
    load();
    api.get('/lab/codediff/list?limit=50')
      .then(({ data }) => setDiffs(data.diffs || []))
      .catch(() => {});
  }, []);

  const build = async () => {
    if (!diffId || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/lab/preview/build/${diffId}`);
      toast.success(`Preview built · ${data.status}`);
      setSelectedId(data.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Build failed');
    } finally {
      setBusy(false);
    }
  };

  const selected = useMemo(
    () => previews.find((p) => p.id === selectedId),
    [previews, selectedId],
  );

  return (
    <div data-testid="lab-preview" className="space-y-3">
      {/* INPUT */}
      <div className="border border-cyan-sys p-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">
          // build sandboxed preview
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <select
            data-testid="lab-preview-diff-select"
            value={diffId}
            onChange={(e) => setDiffId(e.target.value)}
            disabled={!isProd || busy}
            className="flex-1 bg-transparent border border-[var(--border-default)] px-2 py-2 text-[11px] font-mono focus:border-cyan-sys outline-none"
          >
            <option value="">— select code diff —</option>
            {diffs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.id.slice(0, 8)} · {(d.summary || 'untitled').slice(0, 60)} · {(d.diffs || []).length} file(s)
              </option>
            ))}
          </select>
          <button
            data-testid="lab-preview-build"
            onClick={build}
            disabled={!isProd || busy || !diffId}
            className="flex items-center justify-center gap-1.5 border border-cyan-sys text-cyan-sys px-3 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Hammer size={11} />}
            {busy ? 'building...' : 'build preview'}
          </button>
        </div>
        <div className="text-[10px] text-[var(--txt-secondary)] mt-2">
          {isProd
            ? 'Lints each diffed file (Python AST + JSX brace check), flags missing/conflicting paths. No filesystem mutation.'
            : '⚠ Preview Builder is production-only. The list below is read-only here.'}
        </div>
      </div>

      {/* LIST + DETAIL */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--txt-secondary)] mb-1">
            {previews.length} preview build(s)
          </div>
          {previews.length === 0 ? (
            <div className="border border-[var(--border-default)] p-4 text-[11px] text-[var(--txt-secondary)]">
              No preview builds yet. Pick a diff above and click “build preview”.
            </div>
          ) : (
            previews.map((p) => (
              <button
                key={p.id}
                data-testid={`lab-preview-${p.id}`}
                onClick={() => setSelectedId(p.id)}
                className={`w-full text-left border p-2.5 transition-colors ${
                  p.id === selectedId
                    ? 'border-cyan-sys bg-[var(--bg-panel-lighter)]'
                    : 'border-[var(--border-default)] hover:border-cyan-sys'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[11px] text-[var(--txt-primary)] truncate font-mono">
                    {p.id.slice(0, 8)} · diff {String(p.diff_id || '').slice(0, 8)}
                  </span>
                  <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_COLOR[p.status] || 'text-[var(--txt-muted)] border-[var(--border-default)]'}`}>
                    {p.status}
                  </span>
                </div>
                <div className="text-[9px] text-[var(--txt-secondary)] truncate">
                  {(p.created_at || '').slice(0, 19).replace('T', ' ')}Z ·
                  {' '}{(p.files || []).length} file(s) ·
                  {' '}{(p.warnings || []).length}w / {(p.errors || []).length}e
                </div>
              </button>
            ))
          )}
        </div>

        {/* DETAIL */}
        <div className="border border-[var(--border-default)] p-3 min-h-[420px]">
          {!selected ? (
            <div className="text-[11px] text-[var(--txt-secondary)]">Select a preview build to inspect.</div>
          ) : (
            <div className="space-y-3 text-[11px]">
              <div className="flex items-center justify-between">
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys">// preview</div>
                <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_COLOR[selected.status] || ''}`}>
                  {selected.status}
                </span>
              </div>
              <div className="text-[10px] font-mono text-[var(--txt-secondary)]">
                preview_id: {selected.id} · diff_id: {selected.diff_id} ·
                by: {selected.created_by}
              </div>

              {(selected.errors || []).length > 0 && (
                <div className="border border-sell text-sell p-2 space-y-0.5">
                  {selected.errors.map((e, i) => (
                    <div key={i} className="text-[10px] font-mono flex gap-1.5">
                      <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {e}
                    </div>
                  ))}
                </div>
              )}
              {(selected.warnings || []).length > 0 && (
                <div className="border border-hold text-hold p-2 space-y-0.5">
                  {selected.warnings.map((w, i) => (
                    <div key={i} className="text-[10px] font-mono flex gap-1.5">
                      <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys">// files</div>
                {(selected.files || []).map((f, i) => (
                  <FileRow key={i} f={f} idx={i} />
                ))}
              </div>

              <div className="pt-2 border-t border-[var(--border-default)] flex items-center justify-between gap-2">
                <div className="text-[10px] text-[var(--txt-secondary)]">
                  Next: run the static test coverage report on this preview.
                </div>
                <button
                  data-testid="lab-preview-goto-tests"
                  onClick={() => onSwitchTab?.('tests')}
                  className="flex items-center gap-1 border border-cyan-sys text-cyan-sys px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black"
                >
                  <FlaskConical size={11} /> open tests
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileRow({ f, idx }) {
  const lintOk = f.lint?.ok !== false;
  return (
    <div className="border border-[var(--border-default)]" data-testid={`lab-preview-file-${idx}`}>
      <div className="px-2 py-1 bg-[var(--bg-panel-lighter)] flex items-center justify-between text-[10px]">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`uppercase tracking-[0.2em] ${
            f.action === 'create' ? 'text-buy' : 'text-cyan-sys'
          }`}>{f.action}</span>
          <span className="font-mono text-[var(--txt-primary)] truncate">{f.file}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${LINT_DOT[String(lintOk)]}`} />
          <span className="text-[var(--txt-muted)] font-mono">
            {f.size_bytes}b · {f.language || '—'}
          </span>
        </div>
      </div>
      {!lintOk && (
        <div className="px-2 py-1 text-[10px] font-mono text-sell border-b border-[var(--border-default)]">
          lint: {f.lint?.error}{f.lint?.line ? ` (line ${f.lint.line})` : ''}
        </div>
      )}
      {f.lint?.note && (
        <div className="px-2 py-1 text-[10px] font-mono text-[var(--txt-secondary)] border-b border-[var(--border-default)]">
          note: {f.lint.note}
        </div>
      )}
      <div className="px-2 py-1 text-[10px] font-mono text-[var(--txt-secondary)] border-b border-[var(--border-default)]">
        exists_in_repo: {String(f.exists_in_repo)}
      </div>
      {f.rationale && (
        <div className="px-2 py-1 text-[10px] text-[var(--txt-secondary)] border-b border-[var(--border-default)]">
          {f.rationale}
        </div>
      )}
      <pre className="p-2 text-[10px] font-mono text-[var(--txt-primary)] whitespace-pre overflow-x-auto max-h-[260px]">
        {f.preview || ''}
      </pre>
    </div>
  );
}
